import { httpClient } from '../httpClient'
import { WorkflowStep3Result } from '../types'

export interface WorkflowStepTableResult {
  result_type: string
  table: {
    columns: string[]
    expected_column_indices: number[]
    rows: {
      values: string[]
      cell_coordinates: {
        xmin: number
        ymin: number
        xmax: number
        ymax: number
      }[]
      confidence_scores: number[]
    }[]
    extraction_metadata: {
      total_rows_detected: number
      successful_extractions: number
      average_confidence: string
      column_mapping_confidence: Record<string, number | string | null>
    }
  }
}

export interface WorkflowStepBoundingBoxResult {
  result_type: 'bounding_box'
  document_bounding_box: {
    doc_id: string
    page_ix: number
    bounding_box_coordinate_type: 'pdf'
    bounding_box: {
      xmin: number
      ymin: number
      xmax: number
      ymax: number
    }
  }
  confidence_score: number
  matched_schedule_found: boolean
  processing_metadata: {
    pages_analyzed: number
    detection_method: string
    processing_time_seconds: number
  }
}

export interface PdfCoordinatesType {
  xmin: number
  ymin: number
  xmax: number
  ymax: number
}

export interface PageAlignmentDetails {
  product_name: string
  table_row_index: number
  aligned_page?: number[] | null
  confidence: number
  match_reasons: string[]
  approved?: boolean
  type: string
  coordinates: PdfCoordinatesType[]
  voltage?: number
  is_edited?: boolean
  comment?: string
}

export interface PageAlignmentProcessingMetadata {
  total_products: number
  successful_alignments: number
  pages_analyzed: number
  average_match_confidence: number
}

export interface WorkflowPageAlignmentStepResult {
  related_pages: number[][]
  result_type: 'page_alignment'
  alignment_details: PageAlignmentDetails[]
  processing_metadata: PageAlignmentProcessingMetadata
}

export interface WorkflowUploadProductDocStepResult {
  result_type: 'upload'
  doc_id: number
}

export type WorkflowStepResult =
  | WorkflowUploadProductDocStepResult
  | WorkflowStepTableResult
  | WorkflowStepBoundingBoxResult
  | WorkflowStepBoundingBoxResult[]
  | WorkflowStep3Result
  | WorkflowPageAlignmentStepResult

export interface WorkflowStepResultParams {
  projectId: number
  workflowInstanceId: string
  stepIndex: number
}

export async function getWorkflowStepResult(params: WorkflowStepResultParams) {
  const { projectId, workflowInstanceId, stepIndex } = params

  const response = await httpClient<WorkflowStepResult>(
    `/api/v2/submittal/projects/${projectId}/workflows/${workflowInstanceId}/step/${stepIndex}/result`,
    {
      method: 'GET'
    }
  )

  return Array.isArray(response) ? response : [response]
}

export const updateWorkflowStepResult = async (
  params: WorkflowStepResultParams & { stepData: { result: WorkflowStepResult } }
) => {
  const { projectId, workflowInstanceId, stepIndex, stepData } = params

  const response = await httpClient<WorkflowStepResult>(
    `/api/v2/submittal/projects/${projectId}/workflows/${workflowInstanceId}/step/${stepIndex}/result`,
    {
      method: 'POST',
      body: JSON.stringify(stepData)
    }
  )

  return response
}

export const getSignedProductsDocument = async (
  projectId: number,
  product_doc_id: number
) => {
  return await httpClient<{ product_doc_url: string }>(
    `/api/v2/submittal/projects/${projectId}/product-doc/${product_doc_id}`,
    {
      method: 'GET'
    }
  )
}
